import { fill } from 'lodash';
import isEmail from 'validator/lib/isEmail';

export enum USER_AGENT {
  MSIE = 'MSIE ',
  TRIDENT = 'Trident/',
  CHROME = 'Chrome',
  FIREFOX = 'Firefox',
  IPHONE = 'iPhone',
  IPAD = 'iPad',
  ANDROID = 'Android',
  MOBILE = '(iPad|iPhone|iPod|Android|BlackBerry)'
}

export enum EVENTS {
  MOUSE_DOWN = 'mousedown',
  TOUCH_START = 'touchstart',
  RESIZE = 'resize',
  SCROLL = 'scroll',
  KEY_DOWN = 'keydown',
  MESSAGE = 'message',
  FOCUS = 'focus',
  BLUR = 'blur'
}

export enum KEY_CODES {
  ENTER = 13,
  SPACE = 32,
  ESCAPE = 27
}

export enum KEY_CHARS {
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  ARROW_UP = 'ArrowUp',
  ARROW_DOWN = 'ArrowDown',
  ENTER = 'Enter',
  SPACE = ' '
}

const regExp1 = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))/;
const regExp2 = /@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/**
 * @deprecated Use isValidEmail() instead for exact alignment with backend validation
 */
export const EMAIL_REGEX = new RegExp(`${regExp1.source}${regExp2.source}`);

/**
 * Validates email using validator.js (same library as backend class-validator).
 * Ensures frontend and backend email validation are aligned.
 * Trims whitespace before validation to handle leading/trailing spaces.
 */
export const isValidEmail = (email: string): boolean => {
  if (!email) {
    return false;
  }
  const trimmedEmail = email.trim();
  return trimmedEmail.length > 0 && isEmail(trimmedEmail);
};
export const POSTAL_CODE_REGEX = new RegExp(/^[a-zA-Z]\d[a-zA-Z] \d[a-zA-Z]\d$/);
export const TRIP_COST_REGEX = new RegExp(/^((\d+)(\.)?(\d{1,2})?)?$/);

export const PHONE_NUMBER_REGEX = new RegExp(/^[0-9\b]+$/);
export const WEB_SITE_URL_REGEX = new RegExp(
  /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&\/\/=]*)/g
);
export const ALPHA_NUMERIC_REGEX = new RegExp(/[^0-9a-zA-Z]/);
export const LETTERS_AND_WHITESPACE_REGEX = new RegExp(/[^\p{L}\s.'\u2019-]/giu);
export const CAN = 'CA';
export const EXCLUDE_CAN = '^(?![CA|CAN|Canada])';
export const TRAVEL_LOCATION_LIMIT = 20;

export const MONERIS_PM_MSG = 'true';
export const MONERIS_DISPLAY_LABELS = 1;
export const MONERIS_ENABLE_EXPIRY = 0;
export const MONERIS_ENABLE_CVD = 0;

export const ORGANIZATION_LIST_LIMIT = 20;

export enum CC_KEYS {
  ID = 'id',
  ENABLE_EXPIRY = 'enable_exp',
  ENABLE_CVD = 'enable_cvd',
  PM_MSG = 'pmmsg',
  DISPLAY_LABELS = 'display_labels',
  CARD_INPUT_LABEL = 'pan_label',
  EXPIRY_INPUT_LABEL = 'exp_label',
  CVD_INPUT_LABEL = 'cvd_label',
  IFRAME_CSS = 'css_body',
  INPUT_CSS = 'css_textbox',
  INPUT_LABEL_CSS = 'css_input_label',
  CC_INPUT_LABEL_CSS = 'css_label_pan',
  EXPIRY_LABEL_CSS = 'css_label_exp',
  CVD_LABEL_CSS = 'css_label_cvd',
  CC_INPUT_CSS = 'css_textbox_pan',
  EXPIRY_INPUT_CSS = 'css_textbox_exp',
  CVD_INPUT_CSS = 'css_textbox_cvd'
}

export const SUPPORTED_CC = ['visa', 'mastercard', 'american-express'];

export enum POLICY_INFO_PAGE {
  ASSOCIATED_POL = 'ASSOCIATED_POL',
  POL_INFO = 'POL_INFO',
  PAYMENT = 'PAYMENT',
  FINANCIAL_TRANS = 'FINANCIAL_TRANS',
  POL_NOTE = 'POL_NOTE',
  POL_DLVR = 'POL_DLVR'
}

export enum ADVISOR_PROFILE_MSG {
  FOUND_IN_TRIPX = 'FOUND_IN_TRIPX',
  NOT_FOUND = 'NOT_FOUND',
  FOUND_IN_LH = 'FOUND_IN_LH',
  FOUND_IN_DSS = 'FOUND_IN_DSS',
  FAILED_TO_RETRIEVE_DSS = 'Failed to retrieve DSS advisor details'
}

export const MLIADV_CODE = 'S901';

export const MAX_TRAVELLERS = 12;
export const FAMILY_PLANS_MIN = 2;
export const FAMILY_PLANS_ADULT = 25;
export const FAMILY_PLANS_MAX_ADULT = 2;
export const FAMILY_PLANS_MAX_AGE = 59;

export const ARIA_HIDDEN = 'aria-hidden';

export const createInputMask = (regex: RegExp, length: number) => {
  return fill(Array(length), regex, 0, length);
};

export const MAX_LENGTH = {
  NAME: 100,
  POSTAL_CODE: 6,
  EMAIL: 100,
  ADDRESS: 100,
  CITY: 100,
  TRIP_LENGTH: 25,
  MODE_OF_TRANSPORTATION: 50,
  PAYMENT_TOKEN: 20,
  PROFILE_CODE: 320,
  PHONE: 14,
  DESTINATION_NAME: 100,
  BANK_TRANSIT_NUMBER: 5,
  ACCOUNT_NUMBER: 13,
  QUBEC_NUMBER: 10,
  AMOUNT_PERCENT: 100,
  PARTY_ID: 10,
  COUNSELLOR_ID: 15
};

export const MIN_LENGTH = {
  CREDIT_CARD: 15,
  QUBEC_NUMBER: 10
};

export const INPUT_MASK = {
  REFERENCEID: 'REF999999999',
  PHONE_NUMBER: '(999) 999-9999',
  POSTAL_CODE: 'a9a 9a9',
  NAME: createInputMask(/[a-zA-Z .\-']/, MAX_LENGTH.NAME),
  NAME_FR_ACCENTS: createInputMask(/[A-Za-zÀ-ÖØ-öø-ÿŒœŸ .,&'\u2019-]/, MAX_LENGTH.NAME),
  PROFILE_CODE: createInputMask(/[a-zA-Z0-9.\-_'@À-ÿ]/, MAX_LENGTH.PROFILE_CODE),
  ADDRESS: createInputMask(/[\p{L}\p{N} .\-',#&@/:\u2018\u2019]/u, MAX_LENGTH.ADDRESS),
  CITY: createInputMask(/[a-zA-Z .\-']/, MAX_LENGTH.CITY),
  MODE_OF_TRANSPORTATION: createInputMask(/[a-zA-Z0-9 ]/, MAX_LENGTH.MODE_OF_TRANSPORTATION),
  TRIP_LENGTH: createInputMask(/[a-zA-Z0-9 ]/, MAX_LENGTH.TRIP_LENGTH),
  PAYMENT_TOKEN: createInputMask(/[a-zA-Z0-9]/, MAX_LENGTH.PAYMENT_TOKEN),
  DESTINATION_NAME: createInputMask(/[a-zA-Z .\-']/, MAX_LENGTH.DESTINATION_NAME),
  CRA_NUMBER: createInputMask(/[0-9]/, 9),
  PARTY_ID: createInputMask(/[0-9]/, MAX_LENGTH.PARTY_ID),
  COUNSELLOR_ID: createInputMask(/[a-zA-Z0-9]/, MAX_LENGTH.COUNSELLOR_ID)
};

export const INPUT_MASK_CHAR = {
  UNDERSCORE: '_',
  NONE: null
};

export const NAV_BAR_USER_ITEMS = {
  USER_LOGOUT: 'signOut',
  MANULIFEID: 'manulifeId',
  PREFERENCES: 'preferences',
  AGENT: 'agentAdminSub',
  LICENSING: 'licensingAdminSub',
  AGENCY: 'agencySubMenu',
  RATES: 'ratesSubMenu',
  ALLOCATIONS: 'allocationsSub',
  DESTINATION_MULTIPLIER: 'destinationMultiplierSub'
};

export const MANULIFE_NAV_ITEMS = [
  NAV_BAR_USER_ITEMS.USER_LOGOUT,
  NAV_BAR_USER_ITEMS.AGENT,
  NAV_BAR_USER_ITEMS.LICENSING,
  NAV_BAR_USER_ITEMS.AGENCY,
  NAV_BAR_USER_ITEMS.RATES,
  NAV_BAR_USER_ITEMS.ALLOCATIONS,
  NAV_BAR_USER_ITEMS.DESTINATION_MULTIPLIER
];

export const DEFAULT_TRIP_COST_RULES = {
  SAIH: {
    minAge: 60,
    minValue: 1500
  }
};